import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import './StoreWalkthrough.css';
import { GiDiamondRing, GiCrystalShine, GiCutDiamond, GiPocketWatch, GiCrownedHeart, GiGems } from 'react-icons/gi';
import { BsWatch, BsGem, BsStars, BsZoomIn, BsArrowLeft, BsCalendar3, BsEnvelope } from 'react-icons/bs';
import { IoSparklesSharp, IoLocationSharp, IoClose } from 'react-icons/io5';
import { FaWalking, FaEye, FaExchangeAlt } from 'react-icons/fa';
import { MdSpeed, Md360 } from 'react-icons/md';
const StoreWalkthrough = () => {
    const videoRef = useRef(null);
    const backgroundVideoRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPath, setCurrentPath] = useState('start');
    const [currentLocation, setCurrentLocation] = useState('entrance');
    const [videoSequenceIndex, setVideoSequenceIndex] = useState(0);
    const [visitedLocations, setVisitedLocations] = useState(new Set());
    const [isReady, setIsReady] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showChoices, setShowChoices] = useState(true);
    const [showInventory, setShowInventory] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0, percentX: 0, percentY: 0 });
    const [videoEnded, setVideoEnded] = useState(false);
    const [footstepVolume, setFootstepVolume] = useState(0.3);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [soundEnabled, setSoundEnabled] = useState(false);
    const audioRef = useRef(null);
    const [isIOSSafari, setIsIOSSafari] = useState(false);
    const [showIOSWarning, setShowIOSWarning] = useState(false);
    // Video sources - Azure Blob Storage
    const videoSources = {
        left1: 'https://blobaneesh.blob.core.windows.net/videos/left1.mp4',
        left2: 'https://blobaneesh.blob.core.windows.net/videos/left2.mp4',
        right1: 'https://blobaneesh.blob.core.windows.net/videos/right1.mp4',
        right2: 'https://blobaneesh.blob.core.windows.net/videos/right2.mp4',
        left1Reverse: 'https://blobaneesh.blob.core.windows.net/videos/left1-reverse-video.mp4',
        left2Reverse: 'https://blobaneesh.blob.core.windows.net/videos/left2-reverse-video.mp4',
        right1Reverse: 'https://blobaneesh.blob.core.windows.net/videos/right1-reverse.mp4',
        right2Reverse: 'https://blobaneesh.blob.core.windows.net/videos/right2-reverse.mp4'
    };
    // Mock inventory data with React Icons
    const inventoryData = {
        diamonds: [
            {
                id: '1',
                name: 'Eternal Brilliance',
                price: '$45,000',
                icon: _jsx(GiDiamondRing, {}),
                description: '3.5 carat round brilliant',
                carats: '3.5ct',
                certification: 'GIA Certified',
                inStock: true
            },
            {
                id: '2',
                name: 'Blue Moon',
                price: '$125,000',
                icon: _jsx(GiCrystalShine, {}),
                description: 'Rare blue diamond, 2.2 carat',
                carats: '2.2ct',
                certification: 'AGS Certified',
                inStock: true
            },
            {
                id: '3',
                name: 'Pink Promise',
                price: '$89,000',
                icon: _jsx(GiCrownedHeart, {}),
                description: 'Pink diamond engagement ring',
                carats: '1.8ct',
                certification: 'GIA Certified',
                inStock: false
            },
            {
                id: '4',
                name: 'Royal Crown',
                price: '$250,000',
                icon: _jsx(GiGems, {}),
                description: 'Diamond tiara, 150 stones',
                carats: '12.5ct total',
                certification: 'IGI Certified',
                inStock: true
            },
            {
                id: '5',
                name: 'Infinity Set',
                price: '$67,000',
                icon: _jsx(BsGem, {}),
                description: 'Necklace and earring set',
                carats: '5.2ct total',
                certification: 'GIA Certified',
                inStock: true
            },
            {
                id: '6',
                name: 'Black Diamond',
                price: '$35,000',
                icon: _jsx(GiCutDiamond, {}),
                description: 'Rare black diamond, 4 carat',
                carats: '4.0ct',
                certification: 'AGS Certified',
                inStock: true
            }
        ],
        watches: [
            {
                id: '7',
                name: 'Patek Philippe',
                price: '$185,000',
                icon: _jsx(BsWatch, {}),
                description: 'Grand Complications',
                certification: 'Swiss Made',
                inStock: true
            },
            {
                id: '8',
                name: 'Rolex Daytona',
                price: '$95,000',
                icon: _jsx(GiPocketWatch, {}),
                description: 'Platinum, Ice Blue Dial',
                certification: 'COSC Certified',
                inStock: false
            },
            {
                id: '9',
                name: 'Audemars Piguet',
                price: '$145,000',
                icon: _jsx(BsWatch, {}),
                description: 'Royal Oak Perpetual',
                certification: 'Swiss Made',
                inStock: true
            },
            {
                id: '10',
                name: 'Richard Mille',
                price: '$750,000',
                icon: _jsx(GiPocketWatch, {}),
                description: 'RM 56-02 Sapphire',
                certification: 'Swiss Made',
                inStock: true
            },
            {
                id: '11',
                name: 'Vacheron Constantin',
                price: '$225,000',
                icon: _jsx(BsWatch, {}),
                description: 'Overseas Tourbillon',
                certification: 'Geneva Seal',
                inStock: true
            },
            {
                id: '12',
                name: 'A. Lange & Söhne',
                price: '$450,000',
                icon: _jsx(GiPocketWatch, {}),
                description: 'Grand Complication',
                certification: 'German Made',
                inStock: false
            }
        ],
        luxury: [
            {
                id: '13',
                name: 'Imperial Necklace',
                price: '$450,000',
                icon: _jsx(GiCrownedHeart, {}),
                description: 'Emerald and diamond masterpiece',
                carats: '45ct total',
                certification: 'GIA Certified',
                inStock: true
            },
            {
                id: '14',
                name: 'Royal Tiara',
                price: '$850,000',
                icon: _jsx(IoSparklesSharp, {}),
                description: 'Victorian era design',
                carats: '75ct total',
                certification: 'Museum Quality',
                inStock: true
            },
            {
                id: '15',
                name: 'Sapphire Suite',
                price: '$325,000',
                icon: _jsx(GiGems, {}),
                description: 'Kashmir sapphires and diamonds',
                carats: '28ct total',
                certification: 'Gubelin Lab',
                inStock: false
            }
        ],
        heritage: [
            {
                id: '16',
                name: 'Art Deco Brooch',
                price: '$125,000',
                icon: _jsx(BsGem, {}),
                description: '1920s platinum and diamond',
                certification: 'Estate Piece',
                inStock: true
            },
            {
                id: '17',
                name: 'Cartier Mystery Clock',
                price: '$485,000',
                icon: _jsx(GiPocketWatch, {}),
                description: '1930s rock crystal',
                certification: 'Cartier Archives',
                inStock: true
            },
            {
                id: '18',
                name: 'Van Cleef Butterfly',
                price: '$275,000',
                icon: _jsx(GiCrystalShine, {}),
                description: 'Ruby and diamond wings',
                carats: '15ct total',
                certification: 'Van Cleef & Arpels',
                inStock: true
            }
        ]
    };
    useEffect(() => {
        // Detect iOS and Safari
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isIOSSafariDetected = isIOS && isSafari;
        setIsIOSSafari(isIOSSafariDetected);
        if (isIOSSafariDetected) {
            setShowIOSWarning(true);
        }
        // Ensure viewport is set for mobile
        const viewport = document.querySelector('meta[name="viewport"]');
        if (!viewport) {
            const meta = document.createElement('meta');
            meta.name = 'viewport';
            meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
            document.head.appendChild(meta);
        }
        // Load background freeze frame
        const loadBackgroundVideo = async () => {
            if (backgroundVideoRef.current) {
                const bgVideo = backgroundVideoRef.current;
                bgVideo.src = videoSources.left1;
                bgVideo.currentTime = 0.1;
                await new Promise((resolve) => {
                    bgVideo.onloadeddata = () => {
                        bgVideo.pause();
                        resolve(true);
                    };
                });
            }
            // iOS Safari: Don't skip loading, let it timeout and show warning
            // if (isIOSSafariDetected) {
            //   console.log('iOS Safari detected: Videos may not load properly');
            // }
            // Enhanced video preloading with quality assurance
            const videoUrls = Object.values(videoSources);
            let loadedCount = 0;
            const totalVideos = videoUrls.length;
            const loadedVideos = new Set();
            const promises = videoUrls.map((src, index) => {
                return new Promise((resolve) => {
                    const video = document.createElement('video');
                    video.src = src;
                    video.preload = 'auto';
                    video.muted = true;
                    // Force highest quality settings
                    video.setAttribute('playsinline', 'true');
                    video.setAttribute('webkit-playsinline', 'true');
                    // iOS-specific loading
                    if (isIOS) {
                        video.setAttribute('autoplay', 'false');
                        video.load(); // Force load on iOS
                    }
                    // Request highest quality
                    if ('requestVideoFrameCallback' in video) {
                        video.requestVideoFrameCallback(() => { });
                    }
                    // Load enough data for smooth playback
                    const handleLoad = () => {
                        if (!loadedVideos.has(src)) {
                            loadedVideos.add(src);
                            loadedCount++;
                            const progress = Math.min(100, Math.round((loadedCount / totalVideos) * 100));
                            setLoadingProgress(progress);
                            // Ensure at least first frame is decoded
                            video.currentTime = 0.1;
                            setTimeout(() => {
                                video.currentTime = 0;
                                resolve(true);
                            }, 100);
                        }
                    };
                    video.oncanplaythrough = handleLoad;
                    video.onloadeddata = () => {
                        // Fallback if canplaythrough doesn't fire
                        setTimeout(handleLoad, 500);
                    };
                    video.onerror = () => {
                        console.error(`Failed to preload: ${src}`);
                        if (!loadedVideos.has(src)) {
                            loadedVideos.add(src);
                            loadedCount++;
                            const progress = Math.min(100, Math.round((loadedCount / totalVideos) * 100));
                            setLoadingProgress(progress);
                        }
                        resolve(false);
                    };
                    // Start loading
                    video.load();
                });
            });
            // Add global timeout to prevent infinite loading
            const timeoutPromise = new Promise((resolve) => {
                setTimeout(() => {
                    console.warn('Video loading timeout - proceeding anyway');
                    resolve('timeout');
                }, 15000); // 15 second max wait
            });
            const results = await Promise.race([
                Promise.all(promises),
                timeoutPromise
            ]);
            // Always mark as ready after loading attempt
            setIsReady(true);
            setIsLoading(false);
            if (results === 'timeout') {
                console.warn('Video preloading timed out');
                setLoadingProgress(100);
            }
            else if (Array.isArray(results)) {
                const allLoaded = results.every(r => r === true);
                if (!allLoaded) {
                    console.warn('Some videos failed to load, but continuing...');
                }
            }
        };
        loadBackgroundVideo().finally(() => {
            // Ensure loading state is cleared even if there's an error
            setIsLoading(false);
        });
        // Mouse position tracking
        const handleMouseMove = (e) => {
            const percentX = (e.clientX / window.innerWidth * 100).toFixed(1);
            const percentY = (e.clientY / window.innerHeight * 100).toFixed(1);
            setMousePos({
                x: e.clientX,
                y: e.clientY,
                percentX: parseFloat(percentX),
                percentY: parseFloat(percentY)
            });
        };
        // Keyboard shortcuts
        const handleKeyPress = (e) => {
            if (e.key === 'Escape') {
                setShowInventory(false);
                setSelectedProduct(null);
            }
            if (e.key === 'i' && videoEnded) {
                setShowInventory(true);
            }
            if (e.key === 'b' && currentLocation !== 'entrance') {
                walkBack();
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyPress);
        // Prevent scroll
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyPress);
            document.body.style.overflow = '';
        };
    }, [currentLocation, videoEnded, showInventory]);
    const playPath = (path) => {
        if (!videoRef.current)
            return;
        setShowChoices(false);
        setCurrentPath(path);
        setIsPlaying(true);
        setVideoEnded(false);
        const video = videoRef.current;
        video.src = videoSources[path];
        video.currentTime = 0;
        video.playbackRate = playbackSpeed;
        // iOS-specific: Force load before play
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (isIOS) {
            video.load();
        }
        // Play footstep sound
        if (soundEnabled)
            playFootsteps();
        // Add small delay for iOS
        const playVideo = async () => {
            try {
                if (isIOS) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
                await video.play();
            }
            catch (error) {
                console.error('Failed to play video:', error);
                setIsPlaying(false);
                setShowChoices(true);
            }
        };
        playVideo();
    };
    const walkBack = () => {
        if (!videoRef.current || currentLocation === 'entrance')
            return;
        setShowInventory(false);
        setSelectedProduct(null);
        setVideoEnded(false);
        setIsPlaying(true);
        const video = videoRef.current;
        const reverseMap = {
            'diamonds': videoSources.left1Reverse,
            'watches': videoSources.right1Reverse,
            'luxury': videoSources.left2Reverse,
            'heritage': videoSources.right2Reverse
        };
        const reverseSrc = reverseMap[currentLocation] || null;
        if (reverseSrc) {
            video.src = reverseSrc;
            video.currentTime = 0;
            video.playbackRate = playbackSpeed;
            // Play footstep sound
            if (soundEnabled)
                playFootsteps();
            video.play().then(() => {
                // Video will trigger onEnded when reverse walk completes
            }).catch(error => {
                console.error('Failed to play reverse video:', error);
                setIsPlaying(false);
            });
        }
    };
    const playFootsteps = () => {
        // Create footstep sound effect (in real app, load actual audio file)
        if (audioRef.current && soundEnabled) {
            audioRef.current.volume = footstepVolume;
            audioRef.current.play().catch(() => { });
        }
    };
    const handleVideoEnd = () => {
        setIsPlaying(false);
        setVideoEnded(true);
        if (videoRef.current) {
            // Keep showing the last frame
            const video = videoRef.current;
            video.pause();
            // Don't reset currentTime to keep last frame visible
        }
        // Determine location based on video that just finished
        if (videoRef.current?.src.includes('reverse')) {
            // Returned to entrance
            setCurrentLocation('entrance');
            setShowChoices(true);
            setVideoEnded(false);
            restartExperience();
        }
        else {
            // Arrived at display based on video path
            const locationMap = {
                'left1': 'diamonds',
                'left2': 'luxury',
                'right1': 'watches',
                'right2': 'heritage'
            };
            const newLocation = locationMap[currentPath] || 'entrance';
            setCurrentLocation(newLocation);
            // Mark location as visited
            setVisitedLocations(prev => new Set(prev).add(newLocation));
            // Automatically show inventory after a short delay
            setTimeout(() => {
                setShowInventory(true);
            }, 1000);
        }
    };
    const restartExperience = () => {
        if (!videoRef.current)
            return;
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        setCurrentPath('start');
        setCurrentLocation('entrance');
        setShowChoices(true);
        setVideoEnded(false);
        setIsPlaying(false);
        setShowInventory(false);
        setSelectedProduct(null);
    };
    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setZoomLevel(1);
    };
    return (_jsxs("div", { className: "store-walkthrough", children: [_jsx("video", { ref: backgroundVideoRef, className: "background-video", playsInline: true, "webkit-playsinline": "true", muted: true, autoPlay: false, style: {
                    display: showChoices && !isPlaying ? 'block' : 'none'
                } }), _jsx("video", { ref: videoRef, className: "walkthrough-video", onEnded: handleVideoEnd, playsInline: true, "webkit-playsinline": "true", muted: true, autoPlay: false, preload: "auto", poster: "", controls: false, style: {
                    opacity: isPlaying || videoEnded ? 1 : 0,
                    visibility: isPlaying || videoEnded ? 'visible' : 'hidden',
                    // Force GPU acceleration
                    transform: 'translateZ(0)',
                    WebkitTransform: 'translateZ(0)'
                } }), _jsx("audio", { ref: audioRef, src: "/sounds/footsteps.mp3" }), isLoading && (_jsx("div", { className: "loading-overlay", children: _jsx("div", { className: "loading-content", children: showIOSWarning && loadingProgress < 100 && !isReady ? (_jsxs(_Fragment, { children: [_jsx("div", { style: { fontSize: '48px', marginBottom: '20px' }, children: "\u26A0\uFE0F" }), _jsx("h2", { children: "Compatibility Notice" }), _jsx("p", { style: { marginBottom: '20px', fontSize: '16px' }, children: "This immersive experience works best in Chrome on iOS." }), _jsx("p", { style: { marginBottom: '30px', fontSize: '14px', opacity: 0.8 }, children: "Safari has limited video support that may prevent the experience from loading properly." }), _jsx("button", { className: "enter-button chrome-button", style: { marginBottom: '15px' }, onClick: () => {
                                    const currentUrl = window.location.href;
                                    const chromeUrl = `googlechrome://${currentUrl.replace(/^https?:\/\//, '')}`;
                                    window.location.href = chromeUrl;
                                    // Fallback if Chrome isn't installed
                                    setTimeout(() => {
                                        window.location.href = 'https://apps.apple.com/app/chrome/id535886823';
                                    }, 1000);
                                }, children: "Open in Chrome" }), _jsx("button", { className: "continue-anyway-button", onClick: () => {
                                    setShowIOSWarning(false);
                                    setIsLoading(false);
                                }, children: "Continue Anyway" }), _jsx("p", { style: { marginTop: '20px', fontSize: '12px', opacity: 0.6 }, children: "If you don't have Chrome, you'll be redirected to download it." })] })) : (_jsxs(_Fragment, { children: [_jsx(IoSparklesSharp, { className: "loading-icon" }), _jsx("h2", { children: "Loading Siebke Hoyt Experience" }), _jsx("div", { className: "loading-progress-container", children: _jsx("div", { className: "loading-progress-bar", style: { width: `${loadingProgress}%` } }) }), _jsxs("p", { className: "loading-percent", children: [loadingProgress, "%"] }), isReady ? (_jsxs("div", { className: "ready-state", children: [_jsx("p", { className: "ready-text", children: "Experience Ready" }), _jsx("button", { className: "enter-button", onClick: () => setIsLoading(false), children: "Enter Showroom" })] })) : (_jsx("p", { children: "Loading high-quality videos..." }))] })) }) })), !isLoading && (_jsxs("div", { className: "speed-control glass-panel", children: [_jsx(MdSpeed, { className: "speed-icon" }), _jsx("span", { children: "Speed:" }), [0.5, 1, 1.5, 2].map(speed => (_jsxs("button", { onClick: () => {
                            setPlaybackSpeed(speed);
                            if (videoRef.current)
                                videoRef.current.playbackRate = speed;
                        }, className: playbackSpeed === speed ? 'active' : '', children: [speed, "x"] }, speed)))] })), _jsxs("div", { className: "persistent-header", children: [_jsx("h1", { className: "luxury-title", children: "Siebke Hoyt" }), _jsx("p", { className: "luxury-subtitle", children: "Fine Jewelers Since 1889" }), _jsx("div", { className: "luxury-accent-line" }), _jsx("div", { className: "title-particles", children: [...Array(8)].map((_, i) => (_jsx("div", { className: "diamond-particle", style: {
                                '--delay': `${i * 0.4}s`,
                                '--duration': `${3 + (i % 2)}s`,
                                '--x': `${-30 + (i * 20)}%`,
                                '--y': `${-20 + (i % 3) * 15}%`
                            } }, i))) })] }), !isLoading && showChoices && (_jsxs("div", { className: "navigation-grid", children: [_jsxs("button", { className: `nav-button ${visitedLocations.has('diamonds') ? 'visited' : ''}`, onClick: () => playPath('left1'), "aria-label": "Walk to diamond display", children: [_jsx("div", { className: "nav-icon", children: _jsx(GiDiamondRing, {}) }), _jsx("h3", { children: "Diamond Collection" }), visitedLocations.has('diamonds') && _jsx("span", { className: "visited-indicator", children: "VISITED" })] }), _jsxs("button", { className: `nav-button ${visitedLocations.has('luxury') ? 'visited' : ''}`, onClick: () => playPath('left2'), "aria-label": "Walk to luxury vault", children: [_jsx("div", { className: "nav-icon", children: _jsx(GiCrownedHeart, {}) }), _jsx("h3", { children: "Luxury Vault" }), visitedLocations.has('luxury') && _jsx("span", { className: "visited-indicator", children: "VISITED" })] }), _jsxs("button", { className: `nav-button ${visitedLocations.has('watches') ? 'visited' : ''}`, onClick: () => playPath('right1'), "aria-label": "Walk to watch display", children: [_jsx("div", { className: "nav-icon", children: _jsx(BsWatch, {}) }), _jsx("h3", { children: "Luxury Watches" }), visitedLocations.has('watches') && _jsx("span", { className: "visited-indicator", children: "VISITED" })] }), _jsxs("button", { className: `nav-button ${visitedLocations.has('heritage') ? 'visited' : ''}`, onClick: () => playPath('right2'), "aria-label": "Walk to heritage collection", children: [_jsx("div", { className: "nav-icon", children: _jsx(GiGems, {}) }), _jsx("h3", { children: "Heritage Collection" }), visitedLocations.has('heritage') && _jsx("span", { className: "visited-indicator", children: "VISITED" })] })] })), showInventory && !isPlaying && (_jsx("div", { className: "inventory-modal", children: _jsxs("div", { className: "inventory-container glass-morph-modal", children: [_jsx("button", { className: "close-btn glass-morph", onClick: () => setShowInventory(false), children: _jsx(IoClose, {}) }), _jsx("h2", { children: currentLocation === 'diamonds' ? 'Siebke Hoyt Diamond Collection' :
                                currentLocation === 'luxury' ? 'Siebke Hoyt Luxury Vault' :
                                    currentLocation === 'watches' ? 'Siebke Hoyt Timepieces' : 'Siebke Hoyt Heritage Collection' }), _jsx("div", { className: "inventory-grid", children: (inventoryData[currentLocation] || []).map(product => (_jsxs("div", { className: `product-card glass-morph ${!product.inStock ? 'out-of-stock' : ''}`, onClick: () => handleProductClick(product), children: [_jsx("div", { className: "product-glow" }), _jsx("div", { className: "product-image", children: product.icon }), _jsx("h3", { children: product.name }), _jsx("p", { className: "product-price", children: product.price }), _jsx("p", { className: "product-desc", children: product.description }), 'carats' in product && product.carats && _jsx("span", { className: "product-carats", children: product.carats }), product.certification && _jsx("span", { className: "product-cert", children: product.certification }), !product.inStock && _jsx("span", { className: "stock-badge", children: "Sold" })] }, product.id))) }), _jsxs("div", { className: "inventory-actions", children: [_jsxs("button", { className: "action-btn glass-button", onClick: walkBack, children: [_jsx("span", { className: "button-shine" }), _jsx(BsArrowLeft, {}), " Return to Showroom"] }), _jsxs("button", { className: "action-btn glass-button explore-other", onClick: () => {
                                        setShowInventory(false);
                                        walkBack();
                                    }, children: [_jsx("span", { className: "button-shine" }), _jsx(FaExchangeAlt, {}), " Explore Other Collection"] })] })] }) })), selectedProduct && (_jsx("div", { className: "product-detail-modal", onClick: () => setSelectedProduct(null), children: _jsxs("div", { className: "product-detail glass-morph-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { className: "close-btn glass-morph", onClick: () => setSelectedProduct(null), children: _jsx(IoClose, {}) }), _jsxs("div", { className: "product-showcase", children: [_jsxs("div", { className: "product-360", children: [_jsx("div", { className: "rotating-product", style: { transform: `scale(${zoomLevel})` }, children: selectedProduct.icon }), _jsxs("div", { className: "view-controls", children: [_jsxs("button", { onClick: () => setZoomLevel(Math.min(zoomLevel + 0.5, 3)), children: [_jsx(BsZoomIn, {}), " Zoom"] }), _jsxs("button", { className: "rotate-hint", children: [_jsx(Md360, {}), " 360\u00B0 View"] })] }), _jsx("div", { className: "sparkle-effects", children: [...Array(8)].map((_, i) => (_jsx(BsStars, { className: "sparkle", style: { '--i': i } }, i))) })] }), _jsxs("div", { className: "product-info", children: [_jsx("h1", { children: selectedProduct.name }), _jsx("p", { className: "price", children: selectedProduct.price }), _jsx("p", { className: "description", children: selectedProduct.description }), _jsxs("div", { className: "product-specs", children: [selectedProduct.carats && (_jsxs("div", { className: "spec", children: [_jsx(GiCutDiamond, {}), " ", selectedProduct.carats] })), selectedProduct.certification && (_jsxs("div", { className: "spec", children: [_jsx(BsGem, {}), " ", selectedProduct.certification] }))] }), selectedProduct.inStock ? (_jsxs("div", { className: "product-actions", children: [_jsxs("button", { className: "btn-primary glass-button", children: [_jsx("span", { className: "button-shine" }), _jsx(BsCalendar3, {}), " Schedule Private Viewing"] }), _jsxs("button", { className: "btn-secondary glass-button", children: [_jsx("span", { className: "button-shine" }), _jsx(BsEnvelope, {}), " Request Details"] })] })) : (_jsxs("div", { className: "sold-out-message", children: [_jsx("p", { children: "This masterpiece has found its home" }), _jsxs("button", { className: "btn-secondary glass-button", children: [_jsx("span", { className: "button-shine" }), _jsx(FaEye, {}), " View Similar Pieces"] })] }))] })] })] }) })), isPlaying && !videoEnded && (_jsxs("div", { className: "playing-indicator glass-panel", children: [_jsx("div", { className: "playing-dot" }), _jsx(FaWalking, { className: "walking-icon" }), _jsxs("span", { children: ["Walking ", videoRef.current?.src.includes('reverse') ? 'back to entrance' : `to ${currentPath === 'left1' ? 'Diamond Collection' :
                                currentPath === 'left2' ? 'Luxury Vault' :
                                    currentPath === 'right1' ? 'Luxury Watches' : 'Heritage Collection'}`] })] })), videoEnded && currentLocation !== 'entrance' && (_jsxs("div", { className: "location-indicator glass-panel", children: [_jsxs("h3", { children: [_jsx(IoLocationSharp, {}), " Current Location: ", currentLocation === 'diamonds' ? 'Diamond Collection' :
                                currentLocation === 'luxury' ? 'Luxury Vault' :
                                    currentLocation === 'watches' ? 'Watch Gallery' : 'Heritage Collection'] }), _jsxs("div", { className: "location-actions", children: [_jsx("button", { onClick: () => setShowInventory(true), children: "View Inventory (I)" }), _jsx("button", { onClick: walkBack, children: "Walk Back (B)" })] })] })), currentLocation === 'diamonds' && (_jsx("div", { className: "ambient-glow diamond-glow" })), currentLocation === 'watches' && (_jsx("div", { className: "ambient-glow watch-glow" }))] }));
};
export default StoreWalkthrough;
